import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BaseService } from '@app/services';
import { DirectivesModule, SharedModule } from '@app/shared';
import { RxwebValidators } from '@rxweb/reactive-form-validators';
import { Subscription } from 'rxjs';
import { AccountsService } from '../accounts.service';
import { PasswordlessAuthService, startRequestCooldown } from '../passwordless';
import { AuthShellComponent } from '../shell';
import { MagicLinkFormInterface, MagicLinkFormValuesInterface } from './magic-link.interface';

/**
 * Requests a Magic Link (then shows the "check your e-mail" state) or consumes the token
 * from the e-mail URL via POST (token never stays in the address bar).
 */
@Component({
  imports: [SharedModule, DirectivesModule, AuthShellComponent],
  selector: 'app-magic-link',
  templateUrl: './magic-link.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MagicLinkComponent extends BaseService implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly accountsService = inject(AccountsService);
  private readonly passwordlessAuthService = inject(PasswordlessAuthService);

  params$: Subscription;
  formMagicLink: FormGroup;
  protected readonly remainingSeconds = signal<number>(0);
  protected readonly isConsumingToken = signal<boolean>(false);
  protected readonly sentTo = signal<string>('');
  private stopCooldown: (() => void) | null = null;

  ngOnInit(): void {
    this.createForm();
    this.params$ = this.route.queryParams.subscribe((params: { token?: string }) => {
      void this.onParamsChange(params);
    });
  }

  ngOnDestroy(): void {
    this.params$.unsubscribe();
    this.stopCooldown?.();
  }

  async onParamsChange(params: { token?: string }): Promise<void> {
    const token: string = params.token?.trim() ?? '';
    if (!token || this.isConsumingToken()) {
      return;
    }
    this.isConsumingToken.set(true);
    await this.router.navigate([], { relativeTo: this.route, queryParams: {}, replaceUrl: true });
    await this.consumeToken(token);
  }

  createForm(): void {
    this.formMagicLink = new FormGroup<MagicLinkFormInterface>({
      email: new FormControl('', [RxwebValidators.required(), RxwebValidators.email()]),
    });
  }

  async requestLink(): Promise<void> {
    if (this.remainingSeconds() > 0) {
      return;
    }
    const formValues: MagicLinkFormValuesInterface = this.formMagicLink.getRawValue();
    this._spin.start();
    await this.passwordlessAuthService
      .requestMagicLink(formValues.email)
      .then(() => {
        this.sentTo.set(formValues.email);
        this.beginCooldown();
      })
      .catch((error: Error) => {
        this._toast.error({ message: error.message });
      })
      .finally(() => {
        this._spin.stop();
      });
  }

  protected changeEmail(): void {
    this.sentTo.set('');
  }

  private async consumeToken(token: string): Promise<void> {
    this._spin.start();
    await this.passwordlessAuthService
      .verifyMagicLink(token)
      .then(async (): Promise<void> => {
        await this.accountsService.loadAccountIds();
        this._toast.success({ message: 'Login realizado com sucesso!' });
        void this.router.navigateByUrl('/dashboard');
      })
      .catch((error: Error) => {
        this._toast.error({ message: error.message });
        this.isConsumingToken.set(false);
      })
      .finally(() => {
        this._spin.stop();
      });
  }

  private beginCooldown(): void {
    this.stopCooldown?.();
    this.stopCooldown = startRequestCooldown((remainingSeconds: number) => this.remainingSeconds.set(remainingSeconds));
  }
}
